import { Capacitor } from '@capacitor/core';
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite';
import { captureException } from '@sentry/react';
import { defineCustomElements as jeepSqlite } from 'jeep-sqlite/loader';

class SqliteConnector {
  private connectionPromise: Promise<SQLiteConnection> | undefined = undefined;

  async connect() {
    if (!this.connectionPromise) {
      this.connectionPromise = this.resolveConnection().catch((e) => {
        console.error(e);

        captureException(e, {
          level: 'error',
          extra: { message: 'Failed to initialize SQLite' },
        });

        throw e;
      });
    }

    return this.connectionPromise;
  }

  /**
   * On web, jeep-sqlite runs sql.js. jeep-sqlite 2.8.0 bundles its own copy of
   * the sql.js JS glue, while vite.config.mts serves `sql-wasm.wasm` from our
   * `sql.js` dependency. The two must come from the same release or opening a
   * database aborts with "LinkError: ... Import #34 "a" "I": function import
   * requires a callable" (https://github.com/sql-js/sql.js/issues/605).
   *
   * Tested against the bundled glue: sql.js 1.11.0 and 1.12.0 work, 1.13.0
   * through 1.15.2 fail. `sql.js` is therefore pinned to 1.12.0 in
   * app/package.json. Lift the pin once jeep-sqlite stops bundling its own
   * glue, or patch it to use the installed sql.js glue.
   */
  private async resolveConnection() {
    const sqlite = new SQLiteConnection(CapacitorSQLite);

    if (!Capacitor.isNativePlatform()) {
      jeepSqlite(window);

      const jeepEl = document.createElement('jeep-sqlite');
      document.body.appendChild(jeepEl);
      await customElements.whenDefined('jeep-sqlite');

      await sqlite.initWebStore();
    }

    return sqlite;
  }
}

export const sqliteConnector = new SqliteConnector();
